import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";

import React from "react";

import { saveSvgAsPng } from "save-svg-as-png";
import { useQueryParams } from "use-query-params";

import ImageDark from "../../../assets/bg-dark.svg"; // relative path to image
import ImageLight from "../../../assets/bg-light.svg"; // relative path to image
import { MaterialIcon } from "../../../components/shared/MaterialIcon";
import { PublicDashboardUrlParams } from "../../../constants";
import { PageCanvas } from "../../../layouts/PageCanvas";
import { PageContainer } from "../../../layouts/PageContainer";
import { PageHeaderPublic } from "../../../layouts/PageHeaderPublic";
import { cloneCanvas, downloadCanvas } from "../../../utils/canvas";
import {
  PublicRegistry,
  PUBLIC_REGISTRY_OPTIONS,
} from "../models/PublicRegistry";
import CaseCountChart from "./CaseCountChart";
import CaseMapChart from "./CaseMapChart";

const CHART_MAP_ID = "map";
const CHART_CASES_ID = "cases";

const StatsDashboard = () => {
  const bgImageSrc = useColorModeValue(ImageLight, ImageDark);

  const [registry, setRegistry] = React.useState<PublicRegistry>();
  const [hiddenCharts, setHiddenCharts] = React.useState<string[]>();

  const toast = useToast();

  const [searchParams, setSearchParams] = useQueryParams();

  const handleHideChart = (chartName: string) => {
    const existingCharts = searchParams[PublicDashboardUrlParams.hideCharts];
    let hiddenChartsUpdate = existingCharts;

    if (
      !existingCharts ||
      (existingCharts && !existingCharts.includes(chartName))
    ) {
      hiddenChartsUpdate = [...existingCharts, chartName];
    }

    setSearchParams({
      [PublicDashboardUrlParams.hideCharts]: hiddenChartsUpdate,
    });
  };

  const handleShowCharts = () => {
    let hiddenChartsUpdate = [];

    setSearchParams({
      [PublicDashboardUrlParams.hideCharts]: hiddenChartsUpdate,
    });
  };

  const handleOnDownloadCanvas = (chartId, filename) => {
    if (!chartId) {
      return;
    }

    // Anchor to the chart ID element
    const chartidElem = document.querySelector(`#${chartId}`);

    if (chartidElem) {
      // Find the canvas html elem under the chart
      const svgListElem = chartidElem.getElementsByTagName("svg");
      if (svgListElem && svgListElem[0]) {
        saveSvgAsPng(svgListElem[0], filename);
      }

      const canvasListElem = chartidElem.getElementsByTagName("canvas");

      if (canvasListElem && canvasListElem[0]) {
        // Make it bigger
        const canvasPrime = cloneCanvas(canvasListElem[0], 4);

        // Download
        if (canvasPrime) {
          downloadCanvas(canvasPrime, filename);
        }
      }
    }
  };

  // Make case chart
  React.useEffect(() => {
    const registryFromParams = searchParams[PublicDashboardUrlParams.registry];
    const hiddenChartsParams =
      searchParams[PublicDashboardUrlParams.hideCharts];

    const foundRegistry = PUBLIC_REGISTRY_OPTIONS.find(
      (regOption) => regOption.value === registryFromParams
    );

    if (foundRegistry) {
      setRegistry(foundRegistry);
    }

    setHiddenCharts(hiddenChartsParams);
  }, [searchParams]);

  React.useEffect(() => {
    if (registry) {
      toast({
        title: registry.title,
        description: "Loading registry data",
        status: "loading",
        duration: 2000,
        isClosable: true,
      });
    }
  }, [registry, toast]);

  return (
    <PageContainer
    // backgroundImage={`url(${bgImageSrc})`}
    // backgroundSize="cover"
    // backgroundRepeat="no-repeat"
    >
      <PageHeaderPublic></PageHeaderPublic>
      <PageCanvas overflow="auto">
        <Stack px="12" py="8" spacing={8}>
          <Center py={8}>
            <Heading size="2xl">{registry?.title}</Heading>
          </Center>

          {registry && (
            <SimpleGrid
              spacing={8}
              columns={{
                base: 1,
                "2xl": !hiddenCharts?.length ? 2 : 1,
              }}
            >
              {!hiddenCharts?.includes(CHART_CASES_ID) && (
                <Box
                  rounded="lg"
                  bg="bg.main"
                  p={8}
                  id={CHART_CASES_ID}
                  borderWidth="1px"
                  position={"relative"}
                  h={"32rem"}
                >
                  <Text textAlign={"center"} textStyle="header" fontSize="xl">
                    Participant Count
                  </Text>
                  <Box pos={"absolute"} top={4} right={4} zIndex={1}>
                    <ButtonGroup w="full" size="sm" variant={"outline"}>
                      <Button
                        onClick={() => handleHideChart(CHART_CASES_ID)}
                        rightIcon={
                          <MaterialIcon
                            name="disabled_visible"
                            variant="outline"
                            fontSize="1rem"
                          />
                        }
                      >
                        Hide
                      </Button>
                      <Button
                        onClick={() =>
                          handleOnDownloadCanvas(
                            CHART_CASES_ID,
                            "geneial-participant-count.png"
                          )
                        }
                        rightIcon={
                          <MaterialIcon
                            name="file_download"
                            variant="outline"
                            fontSize="1rem"
                          />
                        }
                      >
                        Download
                      </Button>
                    </ButtonGroup>
                  </Box>
                  <Box id={CHART_CASES_ID} h="full">
                    <Center h="full">
                      <CaseCountChart
                        registryAbbr={registry.value}
                      ></CaseCountChart>
                    </Center>
                  </Box>
                </Box>
              )}
              {!hiddenCharts?.includes(CHART_MAP_ID) && (
                <Box
                  rounded="lg"
                  bg="bg.main"
                  p={8}
                  borderWidth="1px"
                  id={CHART_MAP_ID}
                  position={"relative"}
                  h={"32rem"}
                >
                  <Text textAlign={"center"} textStyle="header" fontSize="xl">
                    Participant Location
                  </Text>
                  <Box pos={"absolute"} top={4} right={4} zIndex={1}>
                    <ButtonGroup w="full" size="sm" variant={"outline"}>
                      <Button
                        onClick={() => handleHideChart(CHART_MAP_ID)}
                        rightIcon={
                          <MaterialIcon
                            name="disabled_visible"
                            variant="outline"
                            fontSize="1rem"
                          />
                        }
                      >
                        Hide
                      </Button>
                      <Button
                        onClick={() =>
                          handleOnDownloadCanvas(
                            CHART_MAP_ID,
                            "geneial-participant-location.png"
                          )
                        }
                        rightIcon={
                          <MaterialIcon
                            name="file_download"
                            variant="outline"
                            fontSize="1rem"
                          />
                        }
                      >
                        Download
                      </Button>
                    </ButtonGroup>
                  </Box>
                  <Box id={CHART_MAP_ID} h="full">
                    <Center h="full" minH={64}>
                      <CaseMapChart
                        registryAbbr={registry.value}
                      ></CaseMapChart>
                    </Center>
                  </Box>
                </Box>
              )}
            </SimpleGrid>
          )}

          <Box>
            <ButtonGroup
              w="full"
              size="sm"
              variant={"outline"}
              justifyContent="center"
            >
              {hiddenCharts && hiddenCharts.length > 0 && (
                <Button
                  onClick={handleShowCharts}
                  rightIcon={
                    <MaterialIcon
                      name="show_chart"
                      variant="outline"
                      fontSize="1rem"
                    />
                  }
                >
                  Show all charts
                </Button>
              )}
            </ButtonGroup>
          </Box>
        </Stack>
      </PageCanvas>
    </PageContainer>
  );
};

export default StatsDashboard;
